/**
 * FeaturesSection — 9-card feature grid + a spotlight card on the right
 * showing the three subscription tiers with a "get started" CTA.
 *
 * The `as const` icon array is deliberate — it keeps the key literal so the
 * t(`landing.features.list.${key}.title`) lookup type-checks cleanly.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { landingTokens as T } from '@codex/theme';

import LayersIcon from '@mui/icons-material/Layers';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HistoryIcon from '@mui/icons-material/History';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {
  Section,
  Container,
  SectionHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSub,
  FeaturesGrid,
  FeatureCard,
  FeatureIcon,
  FeatureTitle,
  FeatureBody,
  Spotlight,
  SpotlightTitle,
  SpotlightBody,
  SpotlightList,
  TiersPreview,
  TierPreviewRow,
  TierDot,
  TierName as TPreviewName,
  TierPrice as TPreviewPrice,
  TierPerks as TPreviewPerks,
  BtnPrimary,
} from '../../common/LandingPage.styled';
import { VellumProIconFlat } from '../../../../components/brand';

type FeatureKey =
  | 'tiers'
  | 'email'
  | 'arc'
  | 'noRestrictions'
  | 'protection'
  | 'editor'
  | 'versionHistory'
  | 'scheduled'
  | 'series';

interface FeatureDef {
  icon: ReactElement;
  key: FeatureKey;
  accent: boolean;
}

const FEATURES: readonly FeatureDef[] = [
  { icon: <LayersIcon />, key: 'tiers', accent: true },
  { icon: <MailOutlineIcon />, key: 'email', accent: false },
  { icon: <SendIcon />, key: 'arc', accent: true },
  { icon: <DoneAllIcon />, key: 'noRestrictions', accent: false },
  { icon: <ShieldOutlinedIcon />, key: 'protection', accent: true },
  { icon: <EditNoteIcon />, key: 'editor', accent: false },
  { icon: <HistoryIcon />, key: 'versionHistory', accent: true },
  { icon: <ScheduleSendIcon />, key: 'scheduled', accent: false },
  { icon: <CollectionsBookmarkIcon />, key: 'series', accent: true },
] as const;

type SpotlightPerk = 'perJournal' | 'autoVerify' | 'stripe' | 'cancel';
const SPOTLIGHT_PERKS: readonly SpotlightPerk[] = ['perJournal', 'autoVerify', 'stripe', 'cancel'];

type TierKey = 'reader' | 'supporter' | 'innerCircle';
const TIER_PREVIEW: ReadonlyArray<{ key: TierKey; color: string; price: string }> = [
  { key: 'reader', color: T.text3, price: '$5' },
  { key: 'supporter', color: T.gold, price: '$12' },
  { key: 'innerCircle', color: T.copper, price: '$25' },
];

export function FeaturesSection() {
  const { t } = useTranslation();

  return (
    <Section id="features">
      <Container>
        <SectionHeader>
          <SectionEyebrow>{t('landing.features.eyebrow')}</SectionEyebrow>
          <SectionTitle>{t('landing.features.title')}</SectionTitle>
          <SectionSub>{t('landing.features.sub')}</SectionSub>
        </SectionHeader>

        <FeaturesGrid>
          {FEATURES.map(({ icon, key, accent }) => (
            <FeatureCard key={key} accent={accent}>
              <FeatureIcon>{icon}</FeatureIcon>
              <FeatureTitle>{t(`landing.features.list.${key}.title`)}</FeatureTitle>
              <FeatureBody>{t(`landing.features.list.${key}.body`)}</FeatureBody>
            </FeatureCard>
          ))}
        </FeaturesGrid>

        <Spotlight>
          <div>
            <SectionEyebrow>{t('landing.spotlight.eyebrow')}</SectionEyebrow>
            <SpotlightTitle>{t('landing.spotlight.title')}</SpotlightTitle>
            <SpotlightBody>{t('landing.spotlight.body')}</SpotlightBody>
            <SpotlightList>
              {SPOTLIGHT_PERKS.map((k) => (
                <li key={k}>
                  <VellumProIconFlat
                    size={11}
                    color="#BAA87B"
                    style={{ verticalAlign: 'middle', marginRight: 6 }}
                  />{' '}
                  {t(`landing.spotlight.perks.${k}`)}
                </li>
              ))}
            </SpotlightList>
            <BtnPrimary to="/register">
              {t('landing.spotlight.cta')} <ArrowForwardIcon />
            </BtnPrimary>
          </div>

          <TiersPreview>
            {TIER_PREVIEW.map(({ key, color, price }) => {
              const tier = t(`landing.tiers.${key}`, { returnObjects: true }) as {
                name: string;
                perks: string[];
              };
              return (
                <TierPreviewRow key={key}>
                  <TierDot color={color} />
                  <TPreviewName>{tier.name}</TPreviewName>
                  <TPreviewPrice>{price}/mo</TPreviewPrice>
                  <TPreviewPerks>
                    {tier.perks.map((p) => (
                      <span key={p}>{p}</span>
                    ))}
                  </TPreviewPerks>
                </TierPreviewRow>
              );
            })}
          </TiersPreview>
        </Spotlight>
      </Container>
    </Section>
  );
}

export default FeaturesSection;
